import React from 'react'
import { Box, Skeleton } from '@mui/material'
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart as RBarChart,
  Bar,
  PieChart as RPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { intelColors } from './intelTheme'

// Mockup category palette (red · deep-orange · amber · grey · slate · blue · green) + extensions.
export const CAT_PALETTE = [
  '#c62828', '#e65100', '#f9a825', '#9e9e9e', '#546e7a', '#1565c0', '#2e7d32',
  '#5B2D8E', '#00897b', '#ad1457', '#558b2f', '#0277bd', '#6a1b9a', '#8d6e63', '#ef6c00',
]
export const CHART_PALETTE = CAT_PALETTE

const AXIS_TICK = { fontSize: 11, fill: '#637381' }
const GRID = { strokeDasharray: '3 3', stroke: '#f0f0f0', vertical: false }
const shortFmt = (d) => (typeof d === 'string' && d.length >= 7 ? d.slice(5) : d)

// Centered placeholder so charts don't render as a blank box when there's nothing to plot.
export function EmptyState({ height = 240, msg = 'No data for this period' }) {
  return (
    <Box
      sx={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: intelColors.muted, fontSize: 12.5, border: '1px dashed #e0e0e0',
        borderRadius: '6px', background: '#fafafa',
      }}
    >
      {msg}
    </Box>
  )
}
// True when there's nothing meaningful to plot (no rows, or every value is 0/null).
const allZero = (rows, keys) =>
  !rows || rows.length === 0 || rows.every((r) => keys.every((k) => !r[k]))

/** White card framing each chart. */
export function ChartCard({ title, action, children, loading, height = 240 }) {
  return (
    <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '14px 16px' }}>
      {(title || action) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text }}>{title}</Box>
          {action}
        </Box>
      )}
      {loading ? <Skeleton variant='rounded' animation='wave' height={height} sx={{ borderRadius: '8px' }} /> : children}
    </Box>
  )
}

/** Line / area trend. data: [{ [xKey], [yKey] }]. */
export function TrendLine({ data, xKey = 'date', yKey = 'total', name = 'Value', color, height = 240, area = false, shortDate = true }) {
  const stroke = color || intelColors.primary
  const Chart = area ? AreaChart : LineChart
  if (!data || data.length === 0) return <EmptyState height={height} />
  return (
    <ResponsiveContainer width='100%' height={height}>
      <Chart data={data || []} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={shortDate ? shortFmt : undefined} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
        <RTooltip />
        {area ? (
          <Area type='monotone' dataKey={yKey} name={name} stroke={stroke} strokeWidth={2} fill={stroke} fillOpacity={0.12} dot={{ r: 3, fill: stroke }} />
        ) : (
          <Line type='monotone' dataKey={yKey} name={name} stroke={stroke} strokeWidth={2} dot={{ r: 3, fill: stroke }} />
        )}
      </Chart>
    </ResponsiveContainer>
  )
}

/** Clean donut — ring + right legend + tooltip (no center clutter). data: [{ name, value }]. */
export function Donut({ data, height = 260, colors = CAT_PALETTE }) {
  const rows = data || []
  if (allZero(rows, ['value'])) return <EmptyState height={height} />
  return (
    <ResponsiveContainer width='100%' height={height}>
      <RPieChart>
        <Pie data={rows} dataKey='value' nameKey='name' cx='42%' cy='50%' innerRadius='56%' outerRadius='82%' paddingAngle={1} stroke='#fff' strokeWidth={1}>
          {rows.map((e, i) => (
            <Cell key={i} fill={colors[i % colors.length]} />
          ))}
        </Pie>
        <RTooltip formatter={(v, n) => [v, n]} />
        <Legend
          layout='vertical'
          align='right'
          verticalAlign='middle'
          iconType='circle'
          iconSize={9}
          wrapperStyle={{ fontSize: 10.5, lineHeight: '15px', maxWidth: '46%' }}
        />
      </RPieChart>
    </ResponsiveContainer>
  )
}

/** Single-series bars. Pass `colors` for per-bar coloring, else a single `color`. */
export function Bars({ data, xKey = 'name', yKey = 'value', color = '#1565c0', colors, height = 240 }) {
  const rows = data || []
  if (allZero(rows, [yKey])) return <EmptyState height={height} />
  return (
    <ResponsiveContainer width='100%' height={height}>
      <RBarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid {...GRID} />
        <XAxis
          dataKey={xKey}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: '#e2e8f0' }}
          interval={0}
          angle={-28}
          textAnchor='end'
          height={76}
          tickFormatter={(v) => (typeof v === 'string' && v.length > 16 ? v.slice(0, 15) + '…' : v)}
        />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
        <RTooltip cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
        <Bar dataKey={yKey} radius={[3, 3, 0, 0]} maxBarSize={64}>
          {rows.map((e, i) => (
            <Cell key={i} fill={colors ? colors[i % colors.length] : color} />
          ))}
        </Bar>
      </RBarChart>
    </ResponsiveContainer>
  )
}

/** Stacked bars. data: [{ [xKey], key1, key2, ... }], keys: string[]. */
export function StackedBars({ data, xKey = 'name', keys = [], height = 280, shortDate = false }) {
  if (allZero(data, keys)) return <EmptyState height={height} />
  return (
    <ResponsiveContainer width='100%' height={height}>
      <RBarChart data={data || []} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={shortDate ? shortFmt : undefined} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
        <RTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} iconType='square' iconSize={9} />
        {keys.map((k, i) => (
          <Bar key={k} dataKey={k} stackId='s' fill={CAT_PALETTE[i % CAT_PALETTE.length]} maxBarSize={90} />
        ))}
      </RBarChart>
    </ResponsiveContainer>
  )
}
